import { Box, MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { URLSearchParamsInit, useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { Availability_Status_Enum, Business_Profile_Enum } from '@kheops/graphql';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging } from '@kheops/utils';
import i18n from '../../i18n';
import { currentContextAtom } from '../../state';
import { BaseUnit, Product } from './PackagingPage';

export interface PackagingPageSelectsProps {
  product: Product;
}

export default function PackagingPageSelects({ product }: PackagingPageSelectsProps): React.JSX.Element {
  const { t } = useTranslation(['products']);
  const [searchParams, setSearchParams] = useSearchParams();
  const { realm } = useAtomValue(currentContextAtom);
  const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const baseUnit = useMemo((): BaseUnit => {
    const searchParamsSku = searchParams.get('csu');

    return ((searchParamsSku && product.base_units.find(({ sku }) => parseInt(searchParamsSku, 10) === sku)) || product.base_units[0]) as BaseUnit;
  }, [searchParams.get('csu')]);

  const handleNavigation = useCallback((baseUnitSku: string | null, packagingSku: string | null, replace?: boolean): void => {
    setSearchParams(
      {
        ...searchParams,
        csu: baseUnitSku || '',
        packaging: packagingSku || '',
      } as unknown as URLSearchParamsInit,
      { replace },
    );
  }, [baseUnit]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {(product.base_units.length > 1 || baseUnit.packagings.length > 1) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            gap: 2,
          }}
        >
          {product.base_units.length > 1 && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="labelMedium" sx={{ mb: 0.5 }}>
                {t('products:base_unit_other')} :
              </Typography>
              <Select
                fullWidth
                value={searchParams.get('csu')}
                onChange={(event) => handleNavigation(event.target.value!, searchParams.get('packaging'))}
              >
                {
                  product.base_units.reduce((acc, value) => {
                    if (value.availability === Availability_Status_Enum.Available || realm !== Business_Profile_Enum.Buyer) {
                      acc.push((
                        <MenuItem value={value.sku!} key={value.sku!}>
                          {FormatBaseUnitQuantity(value, 'fr', numberFormat)}
                        </MenuItem>
                      ));
                    }

                    return acc;
                  }, [] as React.JSX.Element[])
                }
              </Select>
            </Box>
          )}
          {baseUnit.packagings.length > 1 && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="labelMedium" sx={{ mb: 0.5 }}>
                {t('common:packaging_other')} :
              </Typography>
              <Select
                fullWidth
                value={searchParams.get('packaging')}
                onChange={(event) => handleNavigation(searchParams.get('csu'), event.target.value!)}
              >
                {
                  baseUnit.packagings.map((value) => (
                    <MenuItem value={value.sku!} key={value.sku!}>
                      {FormatLogisticalPackaging(value, 'fr', baseUnit)}
                    </MenuItem>
                  ))
                }
              </Select>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
